<template>
  <div class="recover-page">
    <section class="recover-container">

      <!-- Franja Gobierno de Chile -->
      <div class="government-strip" aria-hidden="true">
        <span class="government-strip-blue"></span>
        <span class="government-strip-red"></span>
      </div>

      <!-- Encabezado institucional, igual al del login -->
      <header class="institution-header">
        <img
          class="institution-mark"
          :src="require('@/assets/img/logo_gobierno.png')"
          alt="Gobierno de Chile"
        />

        <span>Servicio Nacional de Prevención y Respuesta ante Desastres</span>
      </header>

      <main class="recover-hero">

        <div class="recover-brand">
          <h1>
            Visor <span>GRD</span>
          </h1>

          <p>Gestión del Riesgo de Desastres</p>
        </div>

        <div class="recover-card">

          <h2 class="recover-title">Recuperar contraseña</h2>

          <!-- Paso 1: Ingresar correo -->
          <template v-if="step === 1">
            <p class="recover-hint">Ingresa tu correo de usuario. Te enviaremos un código de verificación.</p>
            <div class="recover-field">
              <label for="rec-username">Correo de usuario</label>
              <input
                id="rec-username"
                v-model="username"
                type="text"
                class="form-control"
                placeholder="correo@ejemplo.cl"
                :disabled="sending"
              />
            </div>
            <p v-if="errorMsg" class="recover-error">{{ errorMsg }}</p>
            <div class="recover-actions">
              <router-link to="/inicio" class="btn btn-secondary">Volver</router-link>
              <button class="btn btn-primary" :disabled="sending || !username" @click="requestCode">
                {{ sending ? 'Enviando…' : 'Enviar código' }}
              </button>
            </div>
          </template>

          <!-- Paso 2: Ingresar código -->
          <template v-if="step === 2">
            <p class="recover-hint">
              Te enviamos un código a <strong>{{ username }}</strong>. Revisa tu bandeja de entrada (puede tardar unos minutos).
            </p>
            <div class="recover-field">
              <label>Código de verificación</label>
              <OTPInput v-model="code" />
            </div>
            <p v-if="errorMsg" class="recover-error">{{ errorMsg }}</p>
            <div class="recover-actions">
              <button class="btn btn-secondary" @click="step = 1; errorMsg = ''">Volver</button>
              <button class="btn btn-primary" :disabled="code.length < 6" @click="proceedToPassword">
                Continuar
              </button>
            </div>
            <p class="recover-resend">
              ¿No llegó el código?
              <button class="link-btn" :disabled="sending" @click="requestCode">
                {{ sending ? 'Enviando…' : 'Reenviar código' }}
              </button>
            </p>
          </template>

          <!-- Paso 3: Nueva contraseña -->
          <template v-if="step === 3">
            <p class="recover-hint">Elige una nueva contraseña segura.</p>

            <div class="recover-field">
              <label for="rec-new-pw">Nueva contraseña</label>
              <div class="password-input-wrap">
                <input
                  id="rec-new-pw"
                  v-model="newPassword"
                  :type="showNewPw ? 'text' : 'password'"
                  class="form-control"
                  name="new_password"
                  data-vv-as="contraseña nueva"
                  :class="{ error: errors.has('new_password') }"
                  v-validate="'required|strong_password'"
                  autocomplete="new-password"
                  placeholder="Ingresa la nueva contraseña"
                />
                <button type="button" class="pw-toggle" @click="showNewPw = !showNewPw" :aria-label="showNewPw ? 'Ocultar' : 'Mostrar'">
                  <font-awesome-icon :icon="showNewPw ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <PasswordStrengthMeter :password="newPassword || ''" />
              <p class="recover-error" v-if="errors.has('new_password')">{{ errors.first('new_password') }}</p>
              <div class="requirements-panel">
                <p class="requirements-panel__title">La contraseña debe tener:</p>
                <PasswordRequirements :password="newPassword || ''" />
              </div>
            </div>

            <div class="recover-field">
              <label for="rec-confirm-pw">Repite la contraseña</label>
              <div class="password-input-wrap">
                <input
                  id="rec-confirm-pw"
                  v-model="confirmPassword"
                  :type="showConfirmPw ? 'text' : 'password'"
                  class="form-control"
                  :class="{ error: confirmError }"
                  autocomplete="new-password"
                  placeholder="Repite la contraseña"
                />
                <button type="button" class="pw-toggle" @click="showConfirmPw = !showConfirmPw" :aria-label="showConfirmPw ? 'Ocultar' : 'Mostrar'">
                  <font-awesome-icon :icon="showConfirmPw ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <p class="recover-error" v-if="confirmError">{{ confirmError }}</p>
            </div>

            <p v-if="errorMsg" class="recover-error">{{ errorMsg }}</p>

            <div class="recover-actions">
              <button class="btn btn-secondary" @click="step = 2; errorMsg = ''">Volver</button>
              <button class="btn btn-primary" :disabled="sending" @click="resetPassword">
                {{ sending ? 'Guardando…' : 'Cambiar contraseña' }}
              </button>
            </div>
          </template>

        </div>
      </main>

      <footer class="recover-bottom-bar">
        <img
          class="recover-footer-logo"
          :src="require('@/assets/img/imago-citiaps-blanco.svg')"
          alt="CITIAPS - Innovación en TI para Aplicaciones Sociales"
        />
      </footer>

    </section>
  </div>
</template>

<script>
import PasswordRequirements from '@/components/PasswordRequirements.vue'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter.vue'
import OTPInput from '@/components/OTPInput.vue'

export default {
  name: 'RecoverPassword',
  components: { PasswordRequirements, PasswordStrengthMeter, OTPInput },
  data() {
    return {
      step: 1,
      username: '',
      code: '',
      newPassword: '',
      confirmPassword: '',
      sending: false,
      errorMsg: '',
      confirmError: '',
      showNewPw: false,
      showConfirmPw: false,
    }
  },
  methods: {
    async requestCode() {
      this.errorMsg = ''
      if (!this.username) return
      this.sending = true
      try {
        await this.$http.post(this.$store.getters.backendurl + 'api/v1/auth/recover', { username: this.username })
        this.step = 2
      } catch (e) {
        this.errorMsg = 'No fue posible enviar el código. Intenta nuevamente.'
      } finally {
        this.sending = false
      }
    },
    proceedToPassword() {
      this.errorMsg = ''
      if (this.code.length < 6) return
      this.step = 3
    },
    async resetPassword() {
      this.errorMsg = ''
      this.confirmError = ''
      if (this.newPassword !== this.confirmPassword) {
        this.confirmError = 'Las contraseñas no coinciden.'
        return
      }
      const valid = await this.$validator.validateAll()
      if (!valid) return
      this.sending = true
      try {
        await this.$http.post(this.$store.getters.backendurl + 'api/v1/auth/reset', {
          username: this.username,
          code: this.code,
          new_password: this.newPassword,
        })
        this.$store.dispatch('toast/show', { message: 'Contraseña cambiada con éxito. Inicia sesión.', type: 'success' })
        this.$router.push('/inicio')
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'No fue posible cambiar la contraseña.'
        this.errorMsg = msg
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.recover-page,
.recover-page * {
  box-sizing: border-box;
}

.recover-page {
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;

  display: flex;
  align-items: stretch;
  justify-content: stretch;

  background: var(--color-mist);
  color: var(--color-graphite);
  font-family: 'Public Sans', system-ui, sans-serif;
}

.recover-container {
  width: 100%;
  min-height: 100vh;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: var(--color-navy);
}

/* Franja Gobierno de Chile */

.government-strip {
  width: 100%;
  height: 5px;

  display: flex;
  flex: none;
}

.government-strip-blue,
.government-strip-red {
  flex: 1;
}

.government-strip-blue {
  background: var(--color-blue);
}

.government-strip-red {
  background: var(--color-red);
}

/* Encabezado institucional */

.institution-header {
  width: 100%;
  min-height: clamp(42px, 3.2vw, 48px);
  padding:
    clamp(7px, 0.8vw, 10px)
    clamp(16px, 1.8vw, 28px);

  display: flex;
  align-items: center;
  gap: clamp(9px, 0.8vw, 12px);
  flex: none;

  background: var(--color-dark);
  border-bottom: 1px solid var(--color-steel);

  color: var(--color-fog);
  font-size: clamp(11px, 0.8vw, 13px);
  font-weight: 600;
  line-height: 1.3;
}

.institution-mark {
  width: auto;
  height: clamp(25px, 2vw, 32px);

  display: block;
  flex: none;
  object-fit: contain;
}

/* Área principal */

.recover-hero {
  flex: 1 1 auto;
  width: 100%;
  /* Anula el `body main { margin: 0 5% }` global: con width 100% ese margen
     desborda el hero a la derecha y descentra la tarjeta (igual que login-hero) */
  margin: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  /* Sin las montañas del login, anclar arriba deja un vacío enorme abajo:
     el bloque marca+tarjeta se centra en el espacio disponible */
  justify-content: center;

  padding: 32px 16px;

  background: var(--color-navy);
}

/* Marca Visor GRD */

.recover-brand {
  margin-bottom: 28px;

  text-align: center;
}

.recover-brand h1 {
  margin: 0;

  color: var(--color-white);
  font-size: clamp(28px, 2vw, 34px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.3px;
}

.recover-brand h1 span {
  color: var(--color-orange);
}

.recover-brand p {
  margin: 6px 0 0;

  color: var(--color-fog);
  font-size: clamp(13px, 0.9vw, 15px);
  font-weight: 500;
  letter-spacing: 0.2px;
}

/* Tarjeta del formulario */

.recover-card {
  width: 100%;
  max-width: 440px;
  padding: 32px 36px 34px;

  border: 0;
  border-radius: 8px;

  background: var(--color-white);

  box-shadow:
    0 8px 24px
    color-mix(
      in srgb,
      var(--color-navy) 25%,
      transparent
    );
}

.recover-title {
  margin: 0 0 20px;
  font-size: 21px;
  font-weight: 700;
  color: var(--color-navy);
}

.recover-hint {
  font-size: 0.88rem;
  color: var(--color-graphite);
  line-height: 1.45;
  margin: 0 0 1.25rem;
}

.recover-field {
  margin-bottom: 1.1rem;

  label {
    display: block;
    margin-bottom: 8px;

    color: var(--color-navy);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
  }
}

.recover-card input.form-control {
  width: 100%;
  min-height: 46px;
  padding: 11px 14px;

  border: 1px solid var(--color-line);
  border-radius: 4px;

  background: var(--color-white);
  color: var(--color-graphite);

  font-family: inherit;
  font-size: 16px;

  transition:
    border-color 0.2s ease,
    outline-color 0.2s ease;

  &::placeholder {
    color: var(--color-ghost);
  }

  &:hover {
    border-color: var(--color-steel);
  }

  &:focus {
    border-color: var(--color-steel);

    outline: 2px solid
      color-mix(
        in srgb,
        var(--color-steel) 24%,
        transparent
      );

    outline-offset: 1px;
  }

  &.error {
    border-color: var(--color-danger);
  }
}

.recover-error {
  color: var(--color-danger);
  font-size: 0.82rem;
  margin: 0.35rem 0 0;
}

/* Acciones: mismos colores y radio que el botón del login */

.recover-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.25rem;
}

.recover-actions .btn {
  min-height: 44px;
  min-width: 0;
  margin: 0;
  padding: 10px 18px;

  border-radius: 4px;

  font-family: inherit;
  font-size: 14px;
  font-weight: 700;

  cursor: pointer;
}

.recover-actions .btn-primary {
  border: 1px solid var(--color-orange);

  background: var(--color-orange);
  color: var(--color-white);

  transition:
    filter 0.2s ease,
    transform 0.1s ease;

  &:hover {
    filter: brightness(0.94);
    opacity: 1;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    border: 1px solid var(--color-orange);
    background: var(--color-orange);
    color: var(--color-white);
    opacity: 0.55;
  }
}

.recover-actions .btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--color-line);

  background: var(--color-white);
  color: var(--color-graphite);

  text-decoration: none;

  &:hover {
    border-color: var(--color-steel);
    background: var(--color-white);
    color: var(--color-navy);
    opacity: 1;
  }
}

.recover-resend {
  margin-top: 1rem;
  font-size: 0.82rem;
  color: var(--color-graphite);
  text-align: center;
}

.link-btn {
  background: none;
  border: none;
  color: var(--color-steel);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  text-decoration: underline;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.code-input {
  letter-spacing: 0.3em;
  font-size: 1.15rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.password-input-wrap {
  position: relative;

  input {
    width: 100%;
    box-sizing: border-box;
    padding-right: 2.5rem;

    // Edge dibuja su propio ojo nativo y quedarian dos superpuestos
    &::-ms-reveal,
    &::-ms-clear {
      display: none;
    }
  }
}

.pw-toggle {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  padding: 0.25rem;
  background: transparent;
  border: none;
  color: var(--color-ghost);
  cursor: pointer;

  &:hover {
    color: var(--color-navy);
  }
}

.requirements-panel {
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--color-mist);
  border-radius: 6px;

  &__title {
    margin: 0 0 0.25rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-graphite);
  }
}

/* Franja inferior institucional */

.recover-bottom-bar {
  width: 100%;
  min-height: clamp(58px, 5vw, 70px);
  padding:
    8px
    clamp(16px, 2vw, 30px);

  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  background: var(--color-dark);
  border-top: 1px solid var(--color-steel);
}

.recover-footer-logo {
  display: block;

  width: clamp(125px, 10vw, 165px);
  height: auto;
  max-height: 52px;

  object-fit: contain;
}

/* Celulares */

@media (max-width: 480px) {
  .institution-header {
    min-height: 40px;
    padding: 7px 12px;

    gap: 8px;
    font-size: 10px;
  }

  .institution-header span {
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .institution-mark {
    height: 23px;
  }

  .recover-brand {
    margin-bottom: 24px;
  }

  .recover-brand h1 {
    font-size: 26px;
  }

  .recover-card {
    max-width: 360px;
    padding: 24px 20px 26px;

    border-radius: 6px;
  }

  .recover-bottom-bar {
    min-height: 56px;
    padding: 8px 12px;
  }

  .recover-footer-logo {
    width: 125px;
    max-height: 44px;
  }
}
</style>
